import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { Flag, History, Loader2, Plus, RotateCcw, SendHorizontal, Sparkles, Trash2 } from 'lucide-react';
import { useProfile } from '../../contexts/ProfileContext';
import { hasAdminPrivileges } from '../../utils/role';
import { api } from '../../services/api';

// AI Chatbot (internal): asisten data saham Bandarmolony. Backend: POST /api/ai-chatbot/chat (routes/ai-chatbot.ts), yang
// memanggil LLM dan alat-alat yang membaca data menu (docs/ai-chatbot-knowledge-base.md). Selama LLM belum dikonfigurasi,
// backend menjawab "masih dalam pengembangan". Jawaban AI dirender sebagai markdown sederhana (tebal, daftar, judul, tabel) lewat
// elemen React: HTML dari jawaban tidak pernah dijalankan dan link tidak dibuat bisa diklik (guardrail 5.9).

// Riwayat disimpan backend di Supabase (percakapan dan pesan per user, dihapus setelah 90 hari tidak dipakai). Panel kiri
// menampilkan daftar percakapan; percakapan terakhir yang dibuka diingat di browser ini supaya terbuka lagi setelah refresh.

/** id: the saved answer (for "jawaban salah"); error: a failed try, shown but never saved; requestId: the question's
 *  id, sent again by "Coba lagi" so the backend can reuse a first try that is still running */
type Msg = { role: 'user' | 'ai'; text: string; error?: boolean; id?: string | undefined; salah?: boolean; requestId?: string };
type Conversation = { id: string; title: string; updated_at: string };

const LAST_CONVERSATION_KEY = 'aiChatbot.lastConversation';
const remember = (id: string | null) => {
  try {
    if (id) localStorage.setItem(LAST_CONVERSATION_KEY, id); else localStorage.removeItem(LAST_CONVERSATION_KEY);
  } catch { /* storage blocked: the page just opens empty next time */ }
};
const remembered = () => {
  try { return localStorage.getItem(LAST_CONVERSATION_KEY); } catch { return null; }
};
const whenLabel = (iso: string) => {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
};

/** **tebal** dan `kode` di dalam satu baris; sisanya teks biasa */
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={i} className="px-1 rounded bg-background">{part.slice(1, -1)}</code>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}

/** Sel satu baris tabel "| a | b |": pipa di ujung boleh tidak ada, "\|" adalah pipa biasa */
function cells(row: string): string[] {
  let s = row.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const out: string[] = [];
  let cell = '';
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '\\' && s[i + 1] === '|') { cell += '|'; i++; }
    else if (s[i] === '|') { out.push(cell.trim()); cell = ''; }
    else cell += s[i];
  }
  out.push(cell.trim());
  return out;
}

/** Baris pemisah di bawah judul tabel: "|---|:--:|---:|" */
const TABLE_DELIMITER = /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?$/;

const alignOf = (d: string): 'left' | 'center' | 'right' =>
  d.startsWith(':') && d.endsWith(':') ? 'center' : d.endsWith(':') ? 'right' : 'left';

/** Paragraf, daftar "- " / "1. ", judul "#", dan tabel "| a | b |" dari jawaban AI */
function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; start: number; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = () => {
    if (para.length) blocks.push(<p key={blocks.length}>{para.map((l, i) => <Fragment key={i}>{i > 0 && <br />}{inline(l)}</Fragment>)}</p>);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const items = list.items.map((it, i) => <li key={i}>{inline(it)}</li>);
    blocks.push(list.ordered
      ? <ol key={blocks.length} start={list.start} className="list-decimal pl-5 space-y-0.5">{items}</ol>
      : <ul key={blocks.length} className="list-disc pl-5 space-y-0.5">{items}</ul>);
    list = null;
  };
  const lines = text.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.trimEnd();
    // a table is a row with pipes, a delimiter row with the same number of cells, then rows until a blank line or a
    // line without a pipe; body rows are padded or cut to the header's width
    const next = lines[i + 1]?.trim() ?? '';
    if (line.includes('|') && TABLE_DELIMITER.test(next) && cells(next).length === cells(line).length) {
      flushPara(); flushList();
      const head = cells(line);
      const align = cells(next).map(alignOf);
      const rows: string[][] = [];
      let j = i + 2;
      for (; j < lines.length && lines[j]!.trim() && lines[j]!.includes('|'); j++) rows.push(cells(lines[j]!));
      blocks.push(
        <div key={blocks.length} className="overflow-x-auto">
          <table className="border-collapse text-sm">
            <thead>
              <tr>{head.map((h, k) => (
                <th key={k} style={{ textAlign: align[k] }} className="px-2 py-1 border border-[#3a4252] bg-muted font-semibold whitespace-nowrap">{inline(h)}</th>
              ))}</tr>
            </thead>
            <tbody>{rows.map((r, n) => (
              <tr key={n}>{head.map((_, k) => (
                <td key={k} style={{ textAlign: align[k] }} className="px-2 py-1 border border-[#3a4252] whitespace-nowrap">{inline(r[k] ?? '')}</td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>,
      );
      i = j - 1;
      continue;
    }
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (bullet || numbered) {
      flushPara();
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) { flushList(); list = { ordered, start: numbered ? Number(numbered[1]) : 1, items: [] }; }
      list.items.push(numbered ? numbered[2]! : bullet![1]!);
    } else if (heading) {
      flushPara(); flushList();
      blocks.push(<p key={blocks.length} className="font-semibold">{inline(heading[1]!)}</p>);
    } else if (!line.trim()) {
      flushPara(); flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara(); flushList();
  return <div className="space-y-2">{blocks}</div>;
}

const EXAMPLES = [
  'Info saham BBCA',
  'Broker mana yang akumulasi BBRI minggu ini?',
  'Saham yang diakumulasi bandar 5 hari terakhir',
  'Siapa pemegang saham terbesar ANTM?',
];

const MAX_QUESTION_CHARS = 1000;

export function StoryAiChatbot() {
  const { profile } = useProfile();
  const canView = hasAdminPrivileges(profile?.role || '');  // admin / developer only while it is being built
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // what the backend is doing right now ("Mengambil data Broker Summary…", "Server AI sedang ramai, mencoba lagi…")
  const [status, setStatus] = useState('');
  const [startedAt, setStartedAt] = useState(0);
  const [now, setNow] = useState(0);
  // history: null = not loaded yet or not available (tables missing), [] = none yet
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [historyNote, setHistoryNote] = useState('');  // a passing history problem, shown in the panel
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [retentionDays, setRetentionDays] = useState(90);
  const [showList, setShowList] = useState(false);  // the history panel on small screens
  const [opening, setOpening] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  // bumped by every open, new conversation and question: a result that arrives after one of those is dropped, so a
  // slow open (the restore on page load included) never replaces what the user did in the meantime
  const epoch = useRef(0);
  // only the latest list load is shown, and never with a conversation deleted here: a load that left before the
  // delete would otherwise put it back
  const listSeq = useRef(0);
  const deleted = useRef(new Set<string>());

  // block body: newer Chrome returns a Promise from scrollIntoView, and an effect may only return a cleanup function
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);
  // seconds counter while waiting, so a slow answer does not look frozen
  useEffect(() => {
    if (!busy) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [busy]);

  /** The panel goes away only when history does not exist (503); any other failure keeps the panel, with a note */
  const loadList = async (): Promise<Conversation[] | null> => {
    const mine = ++listSeq.current;
    const r = await api.aiChatbotConversations();
    if (mine !== listSeq.current) return null;
    if (r.retentionDays) setRetentionDays(r.retentionDays);
    if (r.success && r.data) {
      const list = r.data.filter(c => !deleted.current.has(c.id));
      setConversations(list);
      setHistoryNote('');  // a note about a gone conversation is set after this returns
      return list;
    }
    if (r.status === 503) {
      setConversations(null);
    } else {
      setConversations(list => list ?? []);
      setHistoryNote('Riwayat tidak bisa dimuat, coba lagi nanti.');
    }
    return null;
  };

  const toMessages = (list: { id: string; role: 'user' | 'assistant'; content: string; feedback: string | null }[]): Msg[] =>
    list.map(m => ({ role: m.role === 'user' ? 'user' : 'ai', text: m.content, id: m.role === 'assistant' ? m.id : undefined, salah: m.feedback === 'salah' }));

  const openConversation = async (id: string) => {
    if (busy) return;
    const mine = ++epoch.current;
    setOpening(true);
    setShowList(false);
    const r = await api.aiChatbotConversation(id);
    if (mine !== epoch.current) return;
    setOpening(false);
    if (r.success && r.data) {
      setMessages(toMessages(r.data.messages));
      setConversationId(id);
      remember(id);
      setHistoryNote('');
    } else if (r.status === 404) {
      remember(null);
      await loadList();
      setHistoryNote('Percakapan itu sudah tidak ada.');
    } else {
      setHistoryNote('Percakapan tidak bisa dibuka, coba lagi.');
    }
  };

  // open the history, and the conversation this browser had open last, unless the user has already done something
  useEffect(() => {
    if (!canView) return;
    const start = epoch.current;
    loadList().then(list => {
      const last = remembered();
      if (epoch.current === start && last && list?.some(c => c.id === last)) openConversation(last);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // coming back to this tab: show the conversation as saved, in case another tab added to it, so the screen matches
  // what the AI will take as context
  const resync = useRef<() => void>(() => undefined);
  resync.current = () => {
    if (busy || opening || !conversationId || document.visibilityState !== 'visible') return;
    const mine = epoch.current;
    const id = conversationId;
    api.aiChatbotConversation(id).then(c => {
      if (mine === epoch.current && c.success && c.data) setMessages(m => (m.some(x => x.error) ? m : toMessages(c.data!.messages)));
    });
    loadList();
  };
  useEffect(() => {
    const onVisible = () => resync.current();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const newConversation = () => {
    if (busy) return;
    epoch.current++;
    setOpening(false);
    setMessages([]);
    setConversationId(null);
    remember(null);
    setShowList(false);
  };

  const removeConversation = async (id: string) => {
    if (busy || !window.confirm('Hapus percakapan ini?')) return;
    // gone from the panel at once; the list is reloaded only if the delete fails
    deleted.current.add(id);
    setConversations(list => list && list.filter(c => c.id !== id));
    if (id === conversationId) newConversation();
    const r = await api.aiChatbotDeleteConversation(id);
    if (!r.success) {
      deleted.current.delete(id);
      loadList();
    }
  };

  const ask = async (question: string, requestId: string) => {
    setBusy(true);
    setStatus('AI sedang menjawab…');
    setStartedAt(Date.now());
    setNow(Date.now());
    const r = await api.aiChatbotChat(question, conversationId, requestId, setStatus);
    const mine = epoch.current;
    if (r.success && r.data) {
      const { reply, messageId, conversationId: saved } = r.data;
      setMessages(m => [...m, { role: 'ai', text: reply, id: messageId ?? undefined }]);
      setBusy(false);
      if (saved) {
        setConversationId(saved);
        remember(saved);
        loadList();
      }
      if (saved && messageId) {
        // show the conversation as saved (another tab may have added to it), so the screen holds what the AI will
        // take as context for the next question. Only when this answer was saved: otherwise the saved copy lacks it.
        const c = await api.aiChatbotConversation(saved);
        if (mine === epoch.current && c.success && c.data) setMessages(toMessages(c.data.messages));
      }
      return;
    }
    const error = { role: 'ai' as const, text: (r.error || 'AI tidak bisa menjawab').replace(/[.!]?$/, '.'), error: true };
    if (r.status === 404) {
      // the conversation is gone (deleted elsewhere or past the retention period): keep only this question, and the
      // next try starts a new conversation
      setConversationId(null);
      remember(null);
      setMessages(m => [...m.slice(-1), error]);
      await loadList();
      setHistoryNote('Percakapan itu sudah tidak ada; pertanyaan ini akan memulai percakapan baru.');
    } else {
      // a failed try is shown but not saved, so "Coba lagi" simply asks the same question again
      setMessages(m => [...m, error]);
    }
    setBusy(false);
  };

  const send = (text: string) => {
    const t = text.trim();
    if (!t || busy) return;
    epoch.current++;
    setOpening(false);
    const requestId = crypto.randomUUID();
    setMessages(m => [...m, { role: 'user', text: t, requestId }]);
    setInput('');
    ask(t, requestId);
  };

  /** the last answer failed: drop the error note and ask the same question again, with the same request id */
  const retry = () => {
    const last = [...messages].reverse().find(m => m.role === 'user');
    if (busy || !last) return;
    setMessages(m => m.slice(0, -1));
    ask(last.text, last.requestId ?? crypto.randomUUID());
  };

  /** "Jawaban salah?": flag or unflag a saved answer, shown at once and undone if saving fails */
  const toggleSalah = async (index: number) => {
    const m = messages[index];
    if (!m?.id) return;
    const salah = !m.salah;
    setMessages(list => list.map((x, i) => (i === index ? { ...x, salah } : x)));
    const r = await api.aiChatbotFeedback(m.id, salah);
    if (!r.success) setMessages(list => list.map((x, i) => (i === index ? { ...x, salah: !salah } : x)));
  };

  const waited = Math.max(0, Math.round((now - startedAt) / 1000));

  if (!canView) return <div className="p-6 text-sm text-muted-foreground">Menu ini hanya untuk admin dan developer.</div>;

  const historyPanel = conversations && (
    <aside className={`${showList ? 'flex' : 'hidden'} md:flex absolute md:static inset-y-0 left-0 z-20 w-64 shrink-0 flex-col border-r border-[#3a4252] bg-card`}>
      <div className="p-3">
        <button onClick={newConversation} disabled={busy}
                className="w-full flex items-center justify-center gap-1.5 h-9 rounded-md border border-[#3a4252] text-sm hover:bg-accent disabled:opacity-40">
          <Plus className="w-4 h-4" /> Percakapan baru
        </button>
      </div>
      {historyNote && <p className="px-3 pb-2 text-xs text-[#ffa500]">{historyNote}</p>}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2">
        {conversations.length === 0 && <p className="px-2 py-1 text-xs text-muted-foreground">Belum ada percakapan.</p>}
        {conversations.map(c => (
          <div key={c.id}
               className={`group flex items-center gap-1 rounded-md ${c.id === conversationId ? 'bg-accent' : 'hover:bg-accent/60'}`}>
            <button onClick={() => openConversation(c.id)} disabled={busy} title={c.title}
                    className="flex-1 min-w-0 text-left px-2 py-1.5 disabled:opacity-60">
              <div className="truncate text-sm">{c.title}</div>
              <div className="text-[11px] text-muted-foreground">{whenLabel(c.updated_at)}</div>
            </button>
            <button onClick={() => removeConversation(c.id)} disabled={busy} title="Hapus percakapan"
                    className="p-1.5 mr-1 rounded text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-red-400 disabled:opacity-0">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <p className="px-3 py-2 text-[11px] text-muted-foreground border-t border-[#3a4252]">
        Riwayat disimpan {retentionDays} hari sejak terakhir dipakai.
      </p>
    </aside>
  );

  return (
    <div className="relative flex h-[calc(100vh-64px)]">
      {historyPanel}
      <div className="flex-1 min-w-0 flex flex-col">
      {conversations && (
        <div className="md:hidden flex items-center gap-2 px-4 py-2 border-b border-[#3a4252]">
          <button onClick={() => setShowList(s => !s)} className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <History className="w-4 h-4" /> Riwayat
          </button>
          <button onClick={newConversation} disabled={busy} className="ml-auto flex items-center gap-1 text-sm text-muted-foreground disabled:opacity-40">
            <Plus className="w-4 h-4" /> Baru
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        {opening ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Membuka percakapan…
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center gap-3 max-w-xl mx-auto">
            <Sparkles className="w-8 h-8 text-blue-400" />
            <h2 className="text-lg font-semibold">Tanya data saham ke AI</h2>
            <p className="text-sm text-muted-foreground">Tanya soal saham, broker, atau cari saham dengan kalimat biasa.</p>
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {EXAMPLES.map(e => (
                <button key={e} onClick={() => send(e)}
                        className="px-3 py-1.5 text-xs rounded-full border border-[#3a4252] text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                  {e}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 max-w-3xl mx-auto">
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                m.role === 'user' ? 'self-end bg-blue-600 text-white whitespace-pre-wrap'
                  : m.error ? 'self-start bg-card border border-[#ffa500]/60 text-[#ffa500]'
                  : 'self-start bg-card border border-[#3a4252]'}`}>
                {m.role === 'ai' && !m.error ? <Markdown text={m.text} /> : m.text}
                {m.id && (
                  <button onClick={() => toggleSalah(i)} title={m.salah ? 'Batalkan tanda salah' : 'Tandai jawaban ini salah'}
                          className={`mt-2 flex items-center gap-1 text-[11px] ${m.salah ? 'text-[#ffa500]' : 'text-muted-foreground hover:text-foreground'}`}>
                    <Flag className="w-3 h-3" /> {m.salah ? 'Ditandai salah' : 'Jawaban salah?'}
                  </button>
                )}
                {m.error && i === messages.length - 1 && !busy && (
                  <button onClick={retry}
                          className="mt-2 flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border border-[#ffa500]/60 hover:bg-[#ffa500]/10">
                    <RotateCcw className="w-3.5 h-3.5" /> Coba lagi
                  </button>
                )}
              </div>
            ))}
            {busy && (
              <div className="self-start flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{status}{waited >= 5 ? ` (${waited} detik)` : ''}</span>
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <form onSubmit={e => { e.preventDefault(); send(input); }}
            className="shrink-0 border-t border-[#3a4252] bg-card px-4 py-3">
        <div className="flex items-center gap-2 max-w-3xl mx-auto">
          <input value={input} onChange={e => setInput(e.target.value)} maxLength={MAX_QUESTION_CHARS}
                 placeholder="Contoh: broker mana yang akumulasi BBRI minggu ini?"
                 className="flex-1 h-10 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm" />
          <button type="submit" disabled={!input.trim() || busy || opening} title="Kirim"
                  className="h-10 w-10 flex items-center justify-center rounded-md bg-blue-600 text-white disabled:opacity-40">
            <SendHorizontal className="w-4 h-4" />
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
